import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getPortfolioSummary,
  listPortfolioBatches,
  listPortfolioDeals,
  listDealFacilities,
  getFacilitySummary,
  listPortfolioLoans,
} from '../../api/originatorApi';
import { isUuid } from '../../utils/format';
import { PortfolioKpiCards } from '../../components/portfolio/PortfolioKpiCards';
import { DataTable } from '../../components/ui/DataTable';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { IconSearch } from '../../components/ui/icons';
import { FEATURES } from '../../config/features';
import { originatorPackagePath } from './originatorPackageLabels';


const TABS = [
  { value: 'batch', label: 'Batch' },
  { value: 'deals', label: 'Deal / facility / SPV' },
  { value: 'loans', label: 'Loan' },
];

const PAGE_SIZE = 20;

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <div className="relative flex-1">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-eorig-muted">
        <IconSearch className="h-4 w-4" />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-full border border-eorig-border bg-white py-2 pl-9 pr-3 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
      />
    </div>
  );
}

function TextField({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value.trim())}
      placeholder={placeholder}
      className="rounded-full border border-eorig-border bg-white px-3 py-2 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35 sm:w-52"
    />
  );
}

export function BatchTab() {
  const [search, setSearch] = useState('');
  const [programId, setProgramId] = useState('');
  const [sortBy, setSortBy] = useState('submit_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const programFilter = programId.trim();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listPortfolioBatches({
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          programId: programFilter || undefined,
          sortBy,
          sortOrder,
        });
        if (cancelled) return;
        setRows(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load batches.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, search, programFilter, sortBy, sortOrder, reloadToken]);

  const columns = [
    {
      key: 'batch_id',
      header: 'Batch ID',
      render: (row) => (
        <span className="font-mono text-[12px] font-semibold text-eorig-ink">
          {row.batch_id}
        </span>
      ),
    },
    {
      key: 'program',
      header: 'Program',
      filter: {
        type: 'text',
        value: programId,
        placeholder: 'Program UUID',
        ariaLabel: 'Filter by program',
        onChange: (value) => {
          setPage(1);
          setProgramId(value);
        },
      },
    },
    {
      key: 'submit_date',
      header: 'Submit date',
      render: (row) => (
        <span className="text-eorig-muted">{formatDate(row.submit_date)}</span>
      ),
    },
    { key: 'loans_count', header: 'Loans', sortable: false },
    {
      key: 'authority_state',
      header: 'Authority',
      render: (row) => <StatusPill value={row.authority_state} />,
    },
    {
      key: 'custody_facility_state',
      sortId: 'custody_state',
      header: 'Custody / facility',
      render: (row) => <StatusPill value={row.custody_facility_state} />,
    },
    {
      key: 'next_action',
      header: 'Next action',
      sortable: false,
      render: (row) => (
        <span className="font-medium text-eorig-navy-600">{row.next_action}</span>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <SearchField
        value={search}
        onChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        placeholder="Search batches…"
      />
      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.batch_id}
        rowHref={(row) => originatorPackagePath(row.batch_id)}
        loading={loading}
        emptyMessage="No batches found for the selected filters."
        minWidth={860}
        sortKey={sortBy}
        sortDir={sortOrder}
        onSortChange={(nextKey, nextDir) => {
          setPage(1);
          setSortBy(nextKey);
          setSortOrder(nextDir);
        }}
      />
      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        disabled={loading}
      />
    </div>
  );
}

function FacilitySummaryPanel({ summary }) {
  if (!summary) return null;
  const cells = [
    { label: 'Facility', value: summary.facility_id },
    { label: 'Loans', value: summary.loans },
    { label: 'Pending custody', value: summary.pending_custody },
    { label: 'UPB amount', value: summary.upb_amount },
    { label: 'Servicing retained', value: summary.servicing_retained_count },
  ];
  return (
    <div className="mt-3 grid gap-3 rounded-2xl border border-eorig-border bg-eorig-surface p-4 sm:grid-cols-3 lg:grid-cols-5">
      {cells.map((cell) => (
        <div key={cell.label}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">
            {cell.label}
          </p>
          <p className="mt-1 text-lg font-bold tabular-nums text-eorig-ink">
            {cell.value ?? '—'}
          </p>
        </div>
      ))}
    </div>
  );
}

export function DealsTab() {
  const [level, setLevel] = useState('deals'); // 'deals' | 'facilities'
  const [activeDeal, setActiveDeal] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [facilitySummary, setFacilitySummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');

  const loadDeals = useCallback(async (cancelledRef) => {
    setLoading(true);
    setError('');
    try {
      const response = await listPortfolioDeals();
      if (cancelledRef.cancelled) return;
      setRows(response || []);
    } catch (requestError) {
      if (!cancelledRef.cancelled) setError(requestError.message || 'Unable to load deals.');
    } finally {
      if (!cancelledRef.cancelled) setLoading(false);
    }
  }, []);

  const loadFacilities = useCallback(async (deal, cancelledRef) => {
    setLoading(true);
    setError('');
    try {
      const response = await listDealFacilities(deal.id);
      if (cancelledRef.cancelled) return;
      setRows(response || []);
    } catch (requestError) {
      if (!cancelledRef.cancelled) setError(requestError.message || 'Unable to load facilities.');
    } finally {
      if (!cancelledRef.cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const cancelledRef = { cancelled: false };
    if (level === 'deals') {
      loadDeals(cancelledRef);
    } else if (activeDeal) {
      loadFacilities(activeDeal, cancelledRef);
    }
    return () => {
      cancelledRef.cancelled = true;
    };
  }, [level, activeDeal, reloadToken, loadDeals, loadFacilities]);

  async function openFacilitySummary(facility) {
    setSummaryError('');
    setFacilitySummary(null);
    try {
      const summary = await getFacilitySummary(facility.id);
      setFacilitySummary(summary);
    } catch (requestError) {
      setSummaryError(requestError.message || 'Unable to load facility summary.');
    }
  }

  const isDeals = level === 'deals';
  const columns = [
    { key: 'name', header: 'Name', render: (row) => <span className="font-semibold text-eorig-ink">{row.name}</span> },
    { key: 'type', header: 'Type' },
    { key: 'originator', header: 'Originator' },
    { key: 'loans_count', header: 'Loans' },
    { key: 'current_status', header: 'Status', render: (row) => <StatusPill value={row.current_status} /> },
    { key: 'access_basis', header: 'Access basis' },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (row) => {
        const enabled = isDeals ? isUuid(row.id) : true;
        return (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            disabled={!enabled}
            title={enabled ? undefined : 'Available once the backend returns a valid id'}
            onClick={() => {
              if (!enabled) return;
              if (isDeals) {
                setActiveDeal(row);
                setLevel('facilities');
              } else {
                openFacilitySummary(row);
              }
            }}
          >
            {isDeals ? (enabled ? 'View facilities' : 'Open later') : 'View summary'}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-eorig-ink">
          {isDeals ? 'Deal / facility / SPV view' : `Facilities · ${activeDeal?.name ?? ''}`}
        </h3>
        {!isDeals ? (
          <button
            type="button"
            onClick={() => {
              setLevel('deals');
              setActiveDeal(null);
              setFacilitySummary(null);
              setSummaryError('');
            }}
            className="text-xs font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            ← Back to deals
          </button>
        ) : null}
      </div>

      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.id}-${row.name}`}
        loading={loading}
        emptyMessage="Nothing to show here yet."
        minWidth={860}
      />

      {!isDeals ? (
        <>
          {summaryError ? <ErrorBanner message={summaryError} /> : null}
          <FacilitySummaryPanel summary={facilitySummary} />
        </>
      ) : null}
    </div>
  );
}

export function LoansTab() {
  const [searchParams] = useSearchParams();
  const requestedBatch = searchParams.get('batch') || '';
  const batchId = isUuid(requestedBatch) ? requestedBatch : undefined;

  const [search, setSearch] = useState('');
  const [authorityState, setAuthorityState] = useState('');
  const [custodyState, setCustodyState] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listPortfolioLoans({
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          authorityState: authorityState || undefined,
          custodyState: custodyState || undefined,
          batchId,
        });
        if (cancelled) return;
        setRows(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load loans.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, search, authorityState, custodyState, batchId, reloadToken]);

  const columns = [
    {
      key: 'loan_id',
      header: 'Loan',
      render: (row) => (
        <span className="font-mono text-[12px] font-semibold text-eorig-navy-600">
          {row.loan_id}
        </span>
      ),
    },
    { key: 'borrower', header: 'Borrower' },
    { key: 'batch', header: 'Batch' },
    { key: 'deal_facility_spv', header: 'Deal / facility / SPV' },
    {
      key: 'vault_state',
      header: 'Vault state',
      render: (row) => <StatusPill value={row.vault_state} />,
    },
    { key: 'servicing_access', header: 'Servicing access' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField
          value={search}
          onChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          placeholder="Search loans…"
        />
        <TextField
          value={authorityState}
          onChange={(v) => {
            setPage(1);
            setAuthorityState(v);
          }}
          placeholder="Authority state"
        />
        <TextField
          value={custodyState}
          onChange={(v) => {
            setPage(1);
            setCustodyState(v);
          }}
          placeholder="Custody state"
        />
      </div>
      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowHref={(row) => `/loans/${row.id}`}
        loading={loading}
        emptyMessage="No loans found for the selected filters."
        minWidth={860}
      />
      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        disabled={loading}
      />
    </div>
  );
}

export function PortfolioExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab = TABS.some((t) => t.value === requestedTab) ? requestedTab : 'batch';

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setSummaryLoading(true);
      setSummaryError('');
      try {
        const response = await getPortfolioSummary();
        if (!cancelled) setSummary(response);
      } catch (requestError) {
        if (!cancelled) setSummaryError(requestError.message || 'Unable to load portfolio summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  function selectTab(nextTab) {
    const next = new URLSearchParams();
    next.set('tab', nextTab);
    setSearchParams(next);
  }

  return (
    <div className="space-y-5">
      {summaryError ? (
        <ErrorBanner message={summaryError} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}
      <PortfolioKpiCards summary={summary} loading={summaryLoading} />

      <div className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FilterTabs
            tabs={TABS}
            value={tab}
            ariaLabel="Explorer view"
            onChange={selectTab}
          />
          {FEATURES.bulkActions ? <Button variant="ghost">Bulk actions</Button> : null}
        </div>

        {tab === 'batch' ? <BatchTab /> : null}
        {tab === 'deals' ? <DealsTab /> : null}
        {tab === 'loans' ? <LoansTab /> : null}
      </div>
    </div>
  );
}
